import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { BodyRegular, BodySmall } from '../../atoms/fonts/Body';
import Colours from '../../atoms/Colours';
import { SimpleSpinner } from '../../atoms/SimpleSpinner';
import { OutlineButton } from '../../atoms/buttons/OutlineButton';
import { ButtonSize } from '../../atoms/buttons/ButtonStyling';
import { listMcpApprovedApps, McpApprovedApp, removeMcpApprovedApp } from '@features/settings/services/CompanyService';

/**
 * The MCP sign-in apps a company admin has approved for the company. Apps are approved from the
 * consent page the first time an admin connects one, so there is no add here. Removing one cuts off
 * everyone's connection to it within a few minutes. Render for company admins only.
 */
export function McpApprovedApps() {
  const { t } = useTranslation();
  const [apps, setApps] = useState<McpApprovedApp[] | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listMcpApprovedApps()
      .then(setApps)
      .catch(() => setApps([]));
  }, []);

  const handleRemove = async (app: McpApprovedApp) => {
    if (!window.confirm(t('settings.mcp-integration.approved-apps.remove-confirm', { host: app.host }))) return;
    setError(null);
    setBusy(true);
    try {
      await removeMcpApprovedApp(app.id);
      setApps(prev => prev ? prev.filter(a => a.id !== app.id) : prev);
    } catch (err: any) {
      setError(err?.message || t('settings.mcp-integration.approved-apps.remove-failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section data-test="mcpApprovedApps">
      <BodyRegular weight={600} style={{ marginBottom: 4 }}>{t('settings.mcp-integration.approved-apps.title')}</BodyRegular>
      <BodySmall style={{ marginBottom: 12, color: Colours.darkGrey }}>{t('settings.mcp-integration.approved-apps.note')}</BodySmall>
      {error && <ErrorBox>{error}</ErrorBox>}

      {apps === undefined ? (
        <SimpleSpinner size={32} />
      ) : apps.length === 0 ? (
        <BodySmall style={{ color: Colours.darkGrey }}>{t('settings.mcp-integration.approved-apps.empty')}</BodySmall>
      ) : (
        <List>
          {apps.map(app => (
            <Row key={app.id}>
              <div>
                <Host>{app.host}</Host>
                <BodySmall style={{ color: Colours.darkGrey, wordBreak: 'break-all' }}>{app.redirectUri}</BodySmall>
                <BodySmall style={{ color: Colours.darkGrey }}>
                  {app.clientName ? t('settings.mcp-integration.approved-apps.calls-itself', { name: app.clientName }) + ' · ' : ''}
                  {t('settings.mcp-integration.approved-apps.approved-on', { date: new Date(app.approvedAt).toLocaleDateString() })}
                </BodySmall>
              </div>
              <OutlineButton text={t('settings.mcp-integration.approved-apps.remove')}
                             size={ButtonSize.Small}
                             click={() => handleRemove(app)}
                             disabled={busy} />
            </Row>
          ))}
        </List>
      )}
    </Section>
  );
}

const Section = styled.div`
  margin-top: 16px;
`;

const ErrorBox = styled.div`
  margin-bottom: 12px;
  padding: 8px 12px;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 4px;
  font-size: 13px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  padding: 10px 12px;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  & > button {
    margin-left: auto;
  }
`;

const Host = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  font-weight: 600;
`;
